import { createElement, useMemo, useState } from "react";
import { Moment } from "moment";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiDatePicker, MiTimePicker } from "@miplatform/components";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import {
  ButtonVariants,
  IAttempt,
  IAttemptListItemResponseForWorkOrder,
  IWorkOrder,
  StepType,
  WorkOrderStatus,
} from "@miplatform/types";
import { getNullableDate } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";
import { WorkOrderEmailStepCard } from "../WorkOrderStepCard/WorkOrderEmailStepCard";
import { WorkOrderStepCard } from "../WorkOrderStepCard/WorkOrderStepCard";

export const WorkOrderEdit = ({
  isLoading = false,
  attempt,
  workOrder,
  getAttempt,
  onUpdateAttemptDates,
}: WorkOrderEditProps) => {
  const { classes } = useStyles();
  const [date, setDate] = useState(getNullableDate(attempt.startDate));
  const [startTime, setStartTime] = useState(
    getNullableDate(attempt.startDate)
  );
  const [endTime, setEndTime] = useState(getNullableDate(attempt.endDate));
  const sortedSteps = useMemo(
    () =>
      [...(attempt.steps || [])].sort(
        (a, b) => a.priorityOrder - b.priorityOrder
      ),
    [attempt.steps]
  );
  const canSave = date?.isValid() && startTime?.isValid() && endTime?.isValid();
  const isDirty = useMemo(
    () =>
      date?.isValid() &&
      startTime?.isValid() &&
      endTime?.isValid() &&
      (!date.isSame(getNullableDate(attempt.startDate), "day") ||
        !startTime.isSame(getNullableDate(attempt.startDate), "minute") ||
        !endTime.isSame(getNullableDate(attempt.endDate), "minute")),
    [date, startTime, endTime, attempt.startDate, attempt.endDate]
  );

  const handleSubmit = () => {
    if (!startTime || !endTime || !date) {
      return;
    }

    onUpdateAttemptDates({
      ...attempt,
      startDate: date
        .clone()
        .set({
          hour: startTime.hour(),
          minute: startTime.minute(),
          second: 0,
          millisecond: 0,
        })
        .toISOString(),
      endDate: date
        .clone()
        .set({
          hour: endTime.hour(),
          minute: endTime.minute(),
          second: 0,
          millisecond: 0,
        })
        .toISOString(),
    });
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.section}>
        <Typography className={classes.title}>Work Order Details</Typography>
        <Box width="100%">
          <MiDatePicker
            label="Date"
            value={date}
            format={DATE_TIME_FORMATS.FORMAT_MMMM_D_YYYY}
            onChange={(v: Moment | null) => setDate(v)}
          />
        </Box>
        <Box display="flex" gap={2}>
          <MiTimePicker
            label="Start Time"
            value={startTime}
            format={DATE_TIME_FORMATS.HOURS_MINUTES}
            onChange={(v: Moment | null) => setStartTime(v)}
          />
          <MiTimePicker
            label="Complete Time"
            value={endTime}
            format={DATE_TIME_FORMATS.HOURS_MINUTES}
            onChange={(v: Moment | null) => setEndTime(v)}
          />
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <MiButton
            variant={ButtonVariants.Contained}
            title="Save Changes"
            disabled={!canSave || isLoading || !isDirty}
            onClick={handleSubmit}
          />
        </Box>
      </Box>
      <Box className={classes.stepsContainer}>
        {sortedSteps.map((step, index) =>
          createElement(
            step.stepType === StepType.Email
              ? WorkOrderEmailStepCard
              : WorkOrderStepCard,
            {
              key: step.id,
              number: index + 1,
              step,
              attempt,
              isEditMode: true,
              isLoading,
              isUndoDisabled: workOrder?.status === WorkOrderStatus.Complete,
              onRefresh: getAttempt,
            }
          )
        )}
      </Box>
    </Box>
  );
};

interface WorkOrderEditProps {
  attempt: IAttempt;
  workOrder?: IWorkOrder | null;
  isLoading?: boolean;
  getAttempt: () => Promise<void> | void;
  onUpdateAttemptDates: (attempt: IAttemptListItemResponseForWorkOrder) => void;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    margin: theme.spacing(1, 4),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  section: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    gap: theme.spacing(2),
    width: "100%",
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey.light,
    border: `1px solid ${theme.palette.grey.accent}`,
  },
  title: {
    fontWeight: theme.fontWeight.semiBold,
  },
  description: {
    color: theme.palette.grey.dark,
    fontSize: theme.typography.pxToRem(14),
  },
  stepsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
}));
